/* @flow */
import React, { Component } from 'react'
import { Map, List } from 'immutable'

import CurrentStep from './current_step'
import { makeProps } from './view'
import CancelablePromise from '../cancelable_promise'
import { validateObject, validationHasError } from '../validation/validations'
import i18n from '../helpers/i18n'
import { jsonToState } from '../helpers/json'
import DataHandler from '../data_handler'

import type { PrefixType } from '../helpers/i18n'
import type { PathType, DataType, PathDataType } from '../data_handler'
import type { ChangeStepType, IdType, SendOtpType, VerifyOtpType, UpdateDataType } from './wizard'
import type { ViewPropsType } from './view'
import type { ApiError } from '../api'
import type { ValueMapType } from '../validation/validations'

const transPre = i18n.prefix(['attributes', 'errors'])

type GetValueType = (path: PathType) => mixed
type SetValueType = (path: PathType, value: mixed) => CancelablePromise
type MergeValueType = (path: PathDataType, mergeData: DataType) => CancelablePromise
type PushValueType = (path: PathType, value: mixed) => CancelablePromise
type HandleValueSubType = (value: mixed) => CancelablePromise
type HandleValueType = (path: PathType) => HandleValueSubType
type DeleteValueType = (path: PathType) => CancelablePromise

export type StepPropsType = {
  changeStep: ChangeStepType,
  currentStep: CurrentStep,
  updateData: UpdateDataType,
  data: DataHandler,
  locale: string,
  id: IdType,
  sendOtp: SendOtpType,
  verifyOtp: VerifyOtpType
}

export type StepStateType = {
  data: DataHandler,
  modal: DataHandler,
  propsData: DataHandler
}

/**
 * Root step component Step for the Wizard
 */

class Step<Props: StepPropsType> extends Component<Props, StepStateType> {
  trans: (mixed) => mixed
  getData: GetValueType
  setData: SetValueType
  mergeData: MergeValueType
  handleData: HandleValueType
  pushData: PushValueType
  deleteData: DeleteValueType
  getModal: GetValueType
  setModal: SetValueType
  mergeModal: MergeValueType
  handleModal: HandleValueType
  pushModal: PushValueType
  deleteModal: DeleteValueType

  static mapping: ?Object = null

  sendData = false

  constructor(props: Props) {
    super(props)

    this.state = {
      data: props.data.copy(this),
      modal: new DataHandler({ data: this.initialModal(), component: this }),
      propsData: props.data
    }

    this.getData = this.getValue('data').bind(this)
    this.setData = this.setValue('data').bind(this)
    this.mergeData = this.mergeValue('data').bind(this)
    this.handleData = this.handleValue('data').bind(this)
    this.pushData = this.pushValue('data').bind(this)
    this.deleteData = this.deleteValue('data').bind(this)
    this.getModal = this.getValue('modal').bind(this)
    this.setModal = this.setValue('modal').bind(this)
    this.mergeModal = this.mergeValue('modal').bind(this)
    this.handleModal = this.handleValue('modal').bind(this)
    this.pushModal = this.pushValue('modal').bind(this)
    this.deleteModal = this.deleteValue('modal').bind(this)
  }

  static getDerivedStateFromProps(props: StepPropsType, state: StepStateType) {
    const propsData = props.data && props.data.getData()
    const statePropsData = state.propsData && state.propsData.getData()
    // $FlowFixMe
    if (
      props.data != state.propsData ||
      (Map.isMap(propsData) && !Map.isMap(statePropsData)) ||
      (Map.isMap(propsData) && Map.isMap(statePropsData) && !propsData.equals(statePropsData))
    ) {
      return { data: props.data.copy(state.data && state.data._component), propsData: props.data }
    }
    return null
  }

  initialModal(): Map<string, mixed> {
    return Map({})
  }

  getHandler(state: string = 'data'): DataHandler {
    return this.state[state]
  }

  /**
   * name of the current step
   * @return {string}
   */
  get step(): string {
    return this.currentStep.name
  }

  get displayErrors(): boolean {
    return Boolean(this.getModal(['displayErrors']))
  }

  /**
   * create function to set properties on an input field. sets attributes like name, value, onChange, errorMessage, trans....
   * @param  {string} [state='data'] which data states the field is writing at
   * @param  {object} [indexer=null] if the default indexer or an own is used
   * @return {function}              return a function to call in the input field
   * @example
   * const inputSetProps = step.inputSetProps()
   * <Input {...inputSetProps(['field', 'name'])}/>
   * @example
   * <Input {...inputSetProps(['field', 'name'], { value: customValue })}/>
   * <Input {...inputSetProps(['field', 'name'])} value={customValue}/>
   * @example
   * <Input {...inputSetProps(['field', 'name'], { label: customLabel })}/>
   * <Input {...inputSetProps(['field', 'name'])} label={customLabel}/>
   * @example
   * <Input {...inputSetProps(['field', 'name'], { preProcess: (v) => Number(v) })}/>
   */
  inputSetProps(state: string = 'data', globalOptions: ?Object): (path: PathType, options?: Object) => ViewPropsType {
    const data = this.getHandler(state)
    const validations = state == 'data' ? this.validations(transPre) : this.modalValidations(transPre)
    return makeProps(
      data,
      this.validate(this.displayErrors, state, validations),
      this.getModal(['backendValidations']),
      globalOptions
    )
  }

  /**
   * fetch a value from data or modal state
   * @param  {string} state data|modal
   * @param  {Array}  path  path in the immutable object
   * @return {*}          returns the value stored in the immutable object
   * @example
   * step.getValue('data')(['path', 'to', 'my', 'value'])
   * step.getValue('modal')(['path', 'to', 'my', 'value'])
   * @example
   * step.getDataValue(['path', 'to', 'my', 'value'])
   * step.getModal(['path', 'to', 'my', 'value'])
   */
  getValue(state: string): GetValueType {
    return (path: PathType): mixed => {
      const data = this.getHandler(state)
      return data.getIn(path)
    }
  }

  /**
   * sets a value in the data or modal state
   * @param {string}   state      data|modal
   * @param {Array}    path       array in the immutable object
   * @param {*}      value      value to store
   * @param {function} callback   callback from the setState
   * @return {CancelablePromise}  solved when state is set
   * @example
   * step.setValue('data')(['my', 'path'], 'value')
   * step.setValue('modal')(['my', 'path'], 'value')
   * @example
   * step.setData(['my', 'path'], 'value')
   * step.setModalValue(['my', 'path'], 'value')
   */
  setValue(state: string): SetValueType {
    return (path: PathType, value: mixed) => {
      const data = this.getHandler(state)
      return data.setIn(path, value)
    }
  }

  /**
   * merge an object in the data or modal state
   * @param {string}   state      data|modal
   * @param {Array}    path       array in the immutable object
   * @param {object}   object     object to merge
   * @param {function} callback   callback from the setState
   * @return {CancelablePromise}  solved when state is set
   * @example
   * step.mergeValue('data')(['my', 'path'], { object: 'to merge' })
   * step.mergeValue('modal')(['my', 'path'], { object: 'to merge' })
   * @example
   * step.mergeDataValue(['my', 'path'], { object: 'to merge' })
   * step.mergeModalValue(['my', 'path'], { object: 'to merge' })
   */
  mergeValue(state: string): MergeValueType {
    return (path: PathDataType, mergeData: DataType): CancelablePromise => {
      const data = this.getHandler(state)
      // $FlowFixMe
      return data.update((d) => d.mergeIn(path, mergeData))
    }
  }

  /**
   * handle value for a given path, usefull on callbacks
   * @param {string}   state      data|modal
   * @param {Array}    path       array in the immutable object
   * @param {function} callback   callback from the setState
   * @param {*}      value      object to merge
   * @return {CancelablePromise}  solved when state is set
   * @example
   * step.handleValue('data')(['my', 'path'])('value')
   * step.handleValue('modal')(['my', 'path'])('value')
   * @example
   * step.handleDataValue(['my', 'path'])('value')
   * step.handleModalValue(['my', 'path'])('value')
   */
  handleValue(state: string): HandleValueType {
    return (path: PathType): HandleValueSubType => {
      return (value: mixed): CancelablePromise => {
        const data = this.getHandler(state)
        // $FlowFixMe
        return data.update((d) => d.setIn(path, value))
      }
    }
  }

  /**
   * push a value to an array
   * @param {string}   state      data|modal
   * @param {Array}    path       array in the immutable object
   * @param {*}      value      object to merge
   * @param {function} callback   callback from the setState
   * @return {CancelablePromise}  solved when state is set
   * @example
   * step.pushValue('data')(['my', 'path'], 'value')
   * step.pushValue('modal')(['my', 'path'], 'value')
   * @example
   * step.pushDataValue(['my', 'path'], 'value')
   * step.pushModalValue(['my', 'path'], 'value')
   */
  pushValue(state: string): PushValueType {
    return (path: PathType, value: mixed): CancelablePromise => {
      const data = this.getHandler(state)
      if (List.isList(data.getIn(path))) {
        // $FlowFixMe
        const size = data.getIn(path).size
        const suffixedPath: PathType = (path.concat(size): any)
        // $FlowFixMe
        return data.update((d) => d.setIn(suffixedPath, value))
      } else {
        // $FlowFixMe
        return data.update((d) => d.setIn(path, List([value])))
      }
    }
  }

  /**
   * delete a value to an array
   * @param {string}   state      data|modal
   * @param {Array}    path       array in the immutable object
   * @param {function} callback   callback from the setState
   * @return {CancelablePromise}  solved when state is set
   * @example
   * step.deleteValue('data')(['my', 'path'])
   * step.deleteValue('modal')(['my', 'path'])
   * @example
   * step.deleteDataValue(['my', 'path'])
   * step.deleteModalValue(['my', 'path'])
   */
  deleteValue(state: string): DeleteValueType {
    return (path: PathType) => {
      const data = this.getHandler(state)
      // $FlowFixMe
      return data.update((d) => d.deleteIn(path))
    }
  }

  validations(_v: PrefixType): Object {
    return {}
  }

  modalValidations(_v: PrefixType): Object {
    return {}
  }

  setBackendValidationErrors(errors: CancelablePromise): CancelablePromise {
    if (!this.constructor.mapping) {
      console.error('define "static mappping = Object" to use backend validations')
      return CancelablePromise.resolve()
    }
    return errors.then(jsonToState(this.constructor.mapping)).then((backendValidations) => {
      this.mergeModal(
        [],
        Map({
          displayErrors: Object.keys(errors).length > 0,
          backendValidations
        })
      )
    })
  }

  /**
   * returns name of the next step
   * @return {string}
   * @example step.nextStep
   */
  get nextStep(): string {
    return this.currentStep.nextStep.name
  }

  /**
   * returns name of the previous step
   * @return {string}
   * @example step.previousStep
   */
  get previousStep(): string {
    return this.currentStep.previousStep.name
  }

  /**
   * returns object of the current step
   * @return {CurrentStep}
   * @example step.currentStep
   */
  get currentStep(): CurrentStep {
    return this.props.currentStep
  }

  /**
   * create validation structure with data and validators
   * @param {boolean}  displayErrors
   * @param {string}   state
   * @param {object}   validators
   * @return {object}
   * @example step.currentStep
   */
  validate(displayErrors: boolean, state: string, validations: Object) {
    if (!displayErrors) return {}
    return validateObject(validations, ((this.getHandler(state).getData(): any): ValueMapType))
  }

  /**
   * checks if in the data or modal state some validatins triggered an error message
   * @return {boolean}
   */
  hasError(callback?: () => mixed): boolean {
    const hasError =
      validationHasError(this.validate(true, 'data', this.validations(transPre))) ||
      validationHasError(this.validate(true, 'modal', this.modalValidations(transPre)))
    this.setModal(['displayErrors'], hasError).then(callback)
    return hasError
  }

  /**
   * updates the data in the wizard and calls changeStep to go to given step or step.nextStep
   * @param  {string}   [step=null] next step to show, if not set step.nextStep will be taken
   * @return {CancelablePromise}
   */
  next(step?: string): CancelablePromise {
    if (this.hasError(this.hasErrorCallback)) return CancelablePromise.reject()
    if (this.sendData) {
      return this.serverUpdate(step).then(this.serverUpdateResolve(), this.serverUpdateReject)
    } else {
      const { changeStep, data } = this.props
      return data.replace(this.getHandler()).then(() => {
        return changeStep(step || this.nextStep).then((newStep) => [newStep])
      })
    }
  }

  /**
   * binded function that calls next. should be used with actions
   * @param  {string}   [step=null] next step to show, if not set step.nextStep will be taken
   * @return {CancelablePromise}
   */
  nextHandler = (step?: string): CancelablePromise => {
    return this.next(step)
  }

  /**
   * calls changeStep to go to given step or step.previousStep
   * @param  {string}   [step=null] next step to show, if not set step.nextStep will be taken
   * @return {CancelablePromise}
   */
  previous(step?: string): CancelablePromise {
    const { changeStep } = this.props
    return changeStep(step || this.previousStep)
  }

  /**
   * binded function that calls previous. should be used with actions
   * @param  {string}   [step=null] previous step to show, if not set step.previousStep will be taken
   * @return {CancelablePromise}
   */
  previousHandler = (step?: string): CancelablePromise => {
    return this.previous(step)
  }

  hasErrorCallback = (): void => {}

  serverUpdate(customStep?: string): CancelablePromise {
    const { updateData } = this.props
    return updateData(this.getHandler().getData(), customStep)
  }

  serverUpdateResolve = () => (serverData: DataType) => {
    const { changeStep } = this.props
    const step: ?string = (serverData.get('step'): any)
    return changeStep(step || this.nextStep).then((newStep) => [newStep, serverData])
  }

  serverUpdateReject = (error: ApiError) => {
    // console.error(error)
    if (error.status == 400) {
      return this.setBackendValidationErrors(error.errors || CancelablePromise.resolve(Map())).then(() => {
        return CancelablePromise.reject(error)
      })
    } else {
      alert(i18n.trans(['error', 'network']))
    }
    return CancelablePromise.reject(error)
  }

  onSubmit = (event: Event) => {
    event.preventDefault()
    this.next()
  }

  render(): React$Element<any> {
    return <div>Overwrite this</div>
  }
}

export default Step
